import React, { useState, useEffect } from "react";
import GeneralTable from "../../components/Table/GeneralTable";
import "./LeaveBalanceReport.scss";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const LeaveBalanceReport = () => {
  const [selectedDeptFilter, setSelectedDeptFilter] = useState("all");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Format department: show first 2 and "+X more"
  const formatDepartment = (departments) => {
    if (!departments || departments.length === 0) return "—";
    const filtered = departments.filter((d) => d && d.trim() !== "");
    if (filtered.length <= 2) return filtered.join(", ");
    return `${filtered[0]}, ${filtered[1]} +${filtered.length - 2} more`;
  };

  // Get leave data for specific leave type
  const getLeaveData = (leaveSummary, leaveTypeName) => {
    if (!leaveSummary || leaveSummary.length === 0)
      return { total: "-", used: "-", available: "-" };

    const leave = leaveSummary.find(
      (item) =>
        item.leaveTypeName?.toLowerCase() === leaveTypeName.toLowerCase()
    );

    if (!leave) return { total: "-", used: "-", available: "-" };

    return {
      total: leave.totalLeaves,
      used: leave.usedLeaves,
      available: leave.availableLeaves,
    };
  };

  // Format leave display as "Available/Total (Used: X)"
  const formatLeaveDisplay = (leaveData) => {
    if (leaveData.total === "-") return "-";
    return `${leaveData.available}/${leaveData.total} (Used: ${leaveData.used})`;
  };

  const columns = [
    { header: "Employee", accessor: "emp" },
    { header: "Role", accessor: "role" },
    { header: "Department", accessor: "dept" },
    { header: "Sick Leave", accessor: "sickLeave" },
    { header: "Casual Leave", accessor: "casualLeave" },
    { header: "Privilege Leave", accessor: "privilegeLeave" },
    { header: "Maternity Leave", accessor: "maternityLeave" },
    { header: "Paternity Leave", accessor: "paternityLeave" },
    { header: "Optional Holiday", accessor: "optionalHoliday" },
  ];

  useEffect(() => {
    const fetchLeaveData = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/All`
        );
        if (response.status !== 200) {
          throw new Error("Failed to fetch leave data");
        }

        const result = response.data.data;

        // Map the API data to the table structure
        const mappedData = result.map((leave) => {
          const sickLeave = getLeaveData(leave.leaveSummary, "Sick Leave");
          const casualLeave = getLeaveData(leave.leaveSummary, "Casual Leave");
          const privilegeLeave = getLeaveData(
            leave.leaveSummary,
            "Privilege Leave"
          );
          const maternityLeave = getLeaveData(
            leave.leaveSummary,
            "Maternity Leave"
          );
          const paternityLeave = getLeaveData(
            leave.leaveSummary,
            "Paternity Leave"
          );
          const optionalHoliday = getLeaveData(
            leave.leaveSummary,
            "Optional Holiday"
          );

          return {
            emp: leave.employeeName,
            role: leave.role || "—",
            dept: formatDepartment(leave.department),
            sickLeave: formatLeaveDisplay(sickLeave),
            casualLeave: formatLeaveDisplay(casualLeave),
            privilegeLeave: formatLeaveDisplay(privilegeLeave),
            maternityLeave: formatLeaveDisplay(maternityLeave),
            paternityLeave: formatLeaveDisplay(paternityLeave),
            optionalHoliday: formatLeaveDisplay(optionalHoliday),
          };
        });

        setData(mappedData);

        // Extract unique departments and roles from the data for filtering
        const uniqueDepts = [
          ...new Set(result.flatMap((leave) => leave.department)),
        ];
        const uniqueRoles = [...new Set(result.map((leave) => leave.role))];

        setDepartments(uniqueDepts);
        setRoles(uniqueRoles);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveData();
  }, []);

  const handleDeptFilterChange = (e) => {
    setSelectedDeptFilter(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setSelectedRoleFilter(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const matchesDept =
      selectedDeptFilter === "all" ||
      item.dept.toLowerCase().includes(selectedDeptFilter.toLowerCase());
    const matchesRole =
      selectedRoleFilter === "all" ||
      item.role.toLowerCase() === selectedRoleFilter.toLowerCase();
    return matchesDept && matchesRole;
  });

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="leave-report">
      <h1 className="display6">Leave Balance Summary</h1>
      <div className="leave-report-filters">
        <select
          className="filter-select"
          value={selectedDeptFilter}
          onChange={handleDeptFilterChange}
        >
          <option value="all">All Departments</option>
          {departments.map((dept) => (
            <option key={dept} value={dept.toLowerCase()}>
              {dept}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={selectedRoleFilter}
          onChange={handleRoleFilterChange}
        >
          <option value="all">All Roles</option>
          {roles.map((role) => (
            <option key={role} value={role.toLowerCase()}>
              {role}
            </option>
          ))}
        </select>
      </div>
      <GeneralTable
        columns={columns}
        data={filteredData}
        title="Leave balances"
        initialPageSize={10}
        pageSizeOptions={[10, 25, 50, 100]}
        resetKey={`${selectedDeptFilter}:${selectedRoleFilter}`}
      />
    </div>
  );
};

export default LeaveBalanceReport;
